import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, TrendingUp, Copy, FileDown, MoonStar, Sparkles } from 'lucide-react';
import { RefreshSuggestionDialog } from './RefreshSuggestionDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WINDOWS = [30, 90, 365];

export const UsageTrendsDialog = ({ open, onOpenChange, canManage, onLibraryChanged }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [refreshBlock, setRefreshBlock] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    axios.get(`${API_URL}/api/rfp/boilerplate/usage-trends`, { params: { days }, withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => { toast.error('Could not load usage trends'); setData({ blocks: [] }); });
  }, [open, days]);

  const blocks = data?.blocks || [];
  const active = blocks.filter(b => !b.stale);
  const stale = blocks.filter(b => b.stale);
  const maxTotal = Math.max(1, ...active.map(b => b.window_total));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="rfp-usage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#4ABFCF]" />Capability Usage Trends
          </DialogTitle>
          <DialogDescription>
            Copies and pack inclusions over the selected window — untouched capabilities stand out as stale.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5" data-testid="rfp-usage-window">
          {WINDOWS.map(w => (
            <button key={w} onClick={() => setDays(w)}
              className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${days === w ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'text-slate-600 border-slate-200 hover:border-[#4ABFCF]'}`}
              data-testid={`rfp-usage-window-${w}`}>
              {w === 365 ? '12 months' : `${w} days`}
            </button>
          ))}
        </div>
        {data === null ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5" data-testid="rfp-usage-list">
              {active.length === 0 && (
                <p className="text-xs text-slate-500 py-3 text-center">No usage recorded in this window yet — activity is tracked from now on.</p>
              )}
              {active.map(b => (
                <div key={b.id} className="flex items-center gap-2" data-testid={`rfp-usage-row-${b.id}`}>
                  <span className="font-mono text-[9px] text-slate-400 w-16 shrink-0">{b.capability_id}</span>
                  <span className="text-xs text-[#0F2040] truncate w-48 shrink-0" title={b.title}>{b.title}</span>
                  <div className="flex-1 h-3.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#4ABFCF] rounded-full transition-all"
                      style={{ width: `${Math.max(4, (b.window_total / maxTotal) * 100)}%` }} />
                  </div>
                  <span className="text-[9px] text-slate-500 shrink-0 w-28 text-right"
                    title={`${b.window_copies} copies · ${b.window_includes} pack inclusions in window (lifetime: ${b.lifetime_copies}/${b.lifetime_includes})`}>
                    <Copy className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />{b.window_copies}
                    <FileDown className="w-2.5 h-2.5 inline ml-1.5 mr-0.5 -mt-px" />{b.window_includes}
                  </span>
                </div>
              ))}
            </div>
            {stale.length > 0 && (
              <div data-testid="rfp-usage-stale">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-1.5">
                  <MoonStar className="w-3 h-3" />Stale — no usage in {days === 365 ? '12 months' : `${days} days`}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {stale.map(b => (
                    <span key={b.id} className="inline-flex items-center gap-1">
                      <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200"
                        title={`Lifetime: ${b.lifetime_copies} copies · ${b.lifetime_includes} pack inclusions${b.last_used_at ? ` · last used ${new Date(b.last_used_at).toLocaleDateString()}` : ' · never used'}`}
                        data-testid={`rfp-usage-stale-${b.id}`}>
                        {b.capability_id} {b.title}
                        {canManage && (
                          <button onClick={() => setRefreshBlock(b)}
                            className="ml-1 text-[#4ABFCF] hover:text-[#0F2040]"
                            title="AI refresh — suggest a modernised rewrite"
                            data-testid={`rfp-usage-refresh-${b.id}`}>
                            <Sparkles className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </Badge>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <RefreshSuggestionDialog blockId={refreshBlock?.id} blockTitle={refreshBlock?.title}
          onOpenChange={(v) => { if (!v) setRefreshBlock(null); }}
          onApplied={() => { onLibraryChanged?.(); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
};
